import { makeStyles } from '@material-ui/styles'
import PropTypes from 'prop-types'
import { assoc, omit } from 'ramda'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import uuid from 'uuid'
import UserPasswordField from 'account/components/userManagement/users/UserPasswordField'
import AutocompleteBase from 'core/components/AutocompleteBase'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Input from 'core/elements/input/Input'
import Text from 'core/elements/Text'
import withTooltip from 'core/elements/tooltip/withTooltip'
import { trimInputOnBlur } from 'core/utils/helpers'

const isSensitiveKey = (key) => {
  if (!key) return false
  const keyLower = key.toLowerCase()
  // Auto-detect and mask password/api_key fields
  return keyLower.includes('password') || keyLower.includes('api_key')
}

const useKeyValueStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: ({ showDeleteButton, fullWidth }) =>
      fullWidth
        ? showDeleteButton
          ? '1fr 6px 1fr 32px'
          : '1fr 6px 1fr'
        : showDeleteButton
          ? '172px 6px 172px 32px'
          : '185px 6px 185px',
    gridTemplateRows: '55px',
    gridGap: '12px',
    justifyItems: 'start',
    alignItems: 'center',
  },
  autocomplete: {
    width: '100%',

    '& .MuiFormControl-root': {
      marginTop: 0,
      marginBottom: 0,
      width: '100%',
    },
  },
  minus: {
    fontWeight: 900,
    color: theme.palette.blue.main,
    justifySelf: 'end',
  },
  additionalFields: {
    margin: theme.spacing(2, 0),
    width: '100%',
  },
}))

const KeyValue = ({
  entry = {},
  onChange,
  onDelete,
  keySuggestions = [],
  valueSuggestions = [],
  keyLabel = 'Key',
  valueLabel = 'Value',
  showDeleteButton = true,
  additionalFields = [],
  autoDetectSensitiveKeys = false,
  fullWidth = false,
}) => {
  const [showValue, setShowValue] = useState(false)

  const classes = useKeyValueStyles({ showDeleteButton, fullWidth })
  const handleChange = useCallback(
    (field) => (value) => {
      let updatedEntry = assoc(field, value, entry)
      if (field === 'key' && autoDetectSensitiveKeys) {
        updatedEntry = assoc('sensitive', isSensitiveKey(value), updatedEntry)
      }
      onChange(updatedEntry)
    },
    [entry, onChange, autoDetectSensitiveKeys],
  )
  return (
    <>
      <div className={classes.root}>
        <AutocompleteBase
          inputProps={{ size: 14 }}
          fullWidth
          label={keyLabel}
          value={entry.key}
          initialValue={entry.key}
          onChange={handleChange('key')}
          onBlur={(e) => trimInputOnBlur(e, handleChange('key'))}
          suggestions={keySuggestions}
          disabled={!!entry.disabledKey}
          className={classes.autocomplete}
        />
        <Text variant="body2">-</Text>
        {entry?.sensitive ? (
          <Input
            required
            iconProps={{ placement: 'end', onClick: () => setShowValue(!showValue) }}
            icon={showValue ? 'eye' : 'eye-slash'}
            variant="dark"
            id="value"
            type={showValue ? 'text' : 'password'}
            value={entry.value}
            onChange={(e) => handleChange('value')(e?.target?.value)}
            placeholder={valueLabel}
          />
        ) : (
          <AutocompleteBase
            inputProps={{ size: 14 }}
            fullWidth
            label={valueLabel}
            value={entry.value}
            initialValue={entry.value}
            onChange={handleChange('value')}
            onBlur={(e) => trimInputOnBlur(e, handleChange('value'))}
            suggestions={valueSuggestions}
            className={classes.autocomplete}
            disabled={!!entry.disabledValue}
          />
        )}
        {showDeleteButton && !entry.shouldNotShowDelete && (
          <FontAwesomeIcon className={classes.minus} onClick={onDelete}>
            minus-circle
          </FontAwesomeIcon>
        )}
      </div>
      {additionalFields.map(({ id, label, Component, props }) => (
        <div key={id} className={classes.additionalFields}>
          <Component
            id={id}
            label={label}
            value={entry[id] || ''}
            onChange={handleChange(id)}
            {...props}
          />
        </div>
      ))}
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  addNewEntryContainer: {
    display: 'flex',
    marginTop: theme.spacing(2),
  },
  addNewEntry: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginRight: theme.spacing(2),
  },
  plus: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
    fontWeight: 900,
  },
}))

const newEntry = (sensitive = false) => ({ id: uuid.v4(), key: '', value: '', sensitive })

const initialEntry = newEntry()

// Unfortunately React forces us to use `key` for each item in an
// array and we can't use the index because that will break
// functionality if we delete anything in the middle of the array.
// This forces us to inject an id field into every entry and then
// filter it out before submitting. :(
const addId = (entry, autoDetectSensitiveKeys = false) => {
  const shouldBeSensitive = autoDetectSensitiveKeys && isSensitiveKey(entry.key)
  return {
    ...entry,
    id: uuid.v4(),
    sensitive: entry.sensitive !== undefined ? entry.sensitive : shouldBeSensitive,
  }
}

const KeyValues = ({
  entries: incomingEntries,
  onChange,
  keySuggestions = undefined,
  valueSuggestions = undefined,
  blacklistedTags = [],
  addLabel = 'Add',
  keyLabel = 'Key',
  valueLabel = 'Value',
  allowMultipleValues = true,
  additionalFields = [],
  allowSecretValues = false,
  autoDetectSensitiveKeys = false,
  fullWidth = false,
}) => {
  const classes = useStyles()
  const initialEntriesLoaded = useRef(false)
  const [entries, setEntries] = useState([initialEntry])

  const addBlankEntry = () => setEntries([...entries, newEntry()])
  const addSecretEntry = () => setEntries([...entries, newEntry(true)])
  const deleteEntry = (id) => () => setEntries(entries.filter((x) => x.id !== id))
  const handleChange = useCallback(
    (entry) => {
      setEntries(entries.map((x) => (x.id === entry.id ? entry : x)))
    },
    [entries],
  )

  useEffect(() => {
    // Load initial entries
    if (initialEntriesLoaded.current || incomingEntries === undefined) return
    initialEntriesLoaded.current = true
    const incomingEntriesWithIds = incomingEntries.map((entry) =>
      addId(entry, autoDetectSensitiveKeys),
    )
    allowMultipleValues
      ? setEntries([...incomingEntriesWithIds, ...entries])
      : incomingEntriesWithIds?.length
        ? setEntries([...incomingEntriesWithIds])
        : setEntries([...entries])
  }, [incomingEntries, entries, autoDetectSensitiveKeys])

  useEffect(() => {
    // Remove empty entries and strip out id
    const noEmptyKeys = (x) => x.key.length > 0
    const removeId = (entry) => omit(['id'], entry)
    const sanitized = (entries || []).filter(noEmptyKeys).map(removeId)
    onChange && onChange(sanitized)
  }, [entries])

  const filteredEntries = entries.filter((entry) => !blacklistedTags.includes(entry.key))

  return (
    <div>
      {filteredEntries.map((entry) => (
        <KeyValue
          key={entry.id}
          keySuggestions={keySuggestions}
          valueSuggestions={valueSuggestions}
          entry={entry}
          onChange={handleChange}
          onDelete={deleteEntry(entry.id)}
          keyLabel={keyLabel}
          valueLabel={valueLabel}
          showDeleteButton={allowMultipleValues}
          additionalFields={additionalFields}
          autoDetectSensitiveKeys={autoDetectSensitiveKeys}
          fullWidth={fullWidth}
        />
      ))}
      {allowMultipleValues && (
        <div className={classes.addNewEntryContainer}>
          <div className={classes.addNewEntry} onClick={addBlankEntry}>
            <FontAwesomeIcon className={classes.plus} size="lg">
              plus-circle
            </FontAwesomeIcon>
            <Text variant="body2">{addLabel}</Text>
          </div>
          {allowSecretValues && (
            <div className={classes.addNewEntry} onClick={addSecretEntry}>
              <FontAwesomeIcon className={classes.plus} size="lg">
                plus-circle
              </FontAwesomeIcon>
              <Text variant="body2">Add Secret Variable</Text>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const EntryShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
})

KeyValues.propTypes = {
  keySuggestions: PropTypes.arrayOf(PropTypes.string),
  valueSuggestions: PropTypes.arrayOf(PropTypes.string),
  entries: PropTypes.arrayOf(EntryShape),
  onChange: PropTypes.func,
  blacklistedTags: PropTypes.arrayOf(PropTypes.string),
  allowMultipleValues: PropTypes.bool,
}

export default withTooltip(KeyValues)
